import React, { createRef } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    Paper,
} from '@material-ui/core';
import { FileDropZone, excel } from 'components/layout/mui-dropzone';
import { withStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core/';
import { Constants } from 'utilities/constants';
import { setImportRawData, appNotify, setImportRows, setImportLoadingOverlayState, setErrorListModalState } from 'store/actions';
import PostAcquisitionImportGrid from 'components/admin/postAcquisitionImport/PostAcquisitionImportGrid';
import { importTypeStateSelector, importModuleStateSelector } from 'store/selectors';
import { importPostAcquisitionOpportunity } from 'api/revue/import';
import { LoadingOverlayWrapper } from 'components/layout';
import { handleErrorList } from 'utilities/errorHandlers'
import { setConfirmationModalState } from 'store/actions';

const gridRef = createRef();

const PostAcqImport = ({
    classes,
    setImportRawData,
    appNotify,
    setImportRows,
    importRows,
    history,
    redirectToImportStatusTab,
    setImportLoadingOverlayState,
    importLoadingOverlayState,
    setErrorListModalState,
    setConfirmationModalState,
}) => {

    const maxCountExceeded = importRows?.length > Constants.ImportMaxCount.BACKFILL;

    const FileDropZoneWrapper = ({ children, disabled }) => {
        if (!disabled) return children;

        return <div style={{ position: 'relative' }}>
            <div style={{
                backgroundColor: 'rgb(214 214 214 / 72%)',
                position: 'absolute',
                width: '100%',
                height: '100%'
            }} />
            {children}
        </div>
    }
    const handlePaste = async e => {
        await navigator.permissions.query({ name: 'clipboard-read' });
        try {
            const data = await navigator.clipboard.readText();
            setImportRawData(data, 'paste');
        } catch (err) {
            appNotify(`Paste Error: ${JSON.stringify(err)}`, 'error');
        }
    }

    const handleClearClick = () => {
        setImportRows([]);
    };

    const confirmImport = () => {  
        setConfirmationModalState({ // Open confirmation modal
            isModalOpen: true,
            handleConfirmation: onSubmitImport,
            handleCancellation: closeConfirmationModal,
            titleText: 'Confirm',
            contentText: `Are you sure you want to import ${importRows.length} records?`
          });
    }

    const onSubmitImport = async () => {  
        closeConfirmationModal();
        setImportLoadingOverlayState({
            isLoading: true,
            message: `Loading file...`
        });
        try {
            await importPostAcquisitionOpportunity(importRows);
            appNotify('Import submitted successfully', 'success')
            setImportRows([]);
            redirectToImportStatusTab(history);
        }
        catch (error) {
            handleErrorList('Import', error, setErrorListModalState, appNotify);
        }
        finally {
            setImportLoadingOverlayState({
                isLoading: false,
                message: null,
            });
        }
    }
    
    const onDropZoneFilesAdded = async (addedFiles) => {
        let combinedData = [];
        for (const file of addedFiles) {
            if (excel.excelMimeTypes.indexOf(file.type.toLowerCase()) >= 0) {
                const spreadsheet = await excel.parseExcel(file);
                const DragAndDropData = spreadsheet[Object.keys(spreadsheet)[0]];
                combinedData = combinedData.concat(DragAndDropData);
            }
        }
        setImportRawData(combinedData, 'file');
    }
  
    const closeConfirmationModal = () => {
      setConfirmationModalState({ isModalOpen: false });
    }

    return (
        <LoadingOverlayWrapper
            className={classes.overlay}
            active={importLoadingOverlayState?.isLoading}
            message={importLoadingOverlayState.message}
        >
            <Paper>
                <div id="appMessagesGridContainer" style={{ padding: 20 }}>
                    <div className={classes.container}>
                        <div className={classes.leftControls}>
                            <Button
                                variant="contained"
                                color="primary"
                                style={{ marginLeft: '4px', marginRight: '4px' }}
                                onClick={confirmImport}
                                disabled={importRows?.length < 1 || maxCountExceeded}
                            >
                                Import
                            </Button>
                        </div>
                        <div className={classes.rightControls} >
                            <Button variant='outlined' color='primary' onClick={handlePaste}>
                                Paste from Clipboard
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                style={{ marginLeft: '4px', marginRight: '4px' }}
                                onClick={handleClearClick}
                            > Clear </Button>
                        </div>
                    </div>
                    <FileDropZoneWrapper>
                        <FileDropZone
                            width='100%'
                            acceptedMimeTypes={[...excel.excelMimeTypes, 'text/plain']}
                            onFilesAdded={onDropZoneFilesAdded}
                            elevation={2}
                            dragOverElevation={10}
                            className='gridFileDropZone'
                            style={{ cursor: 'pointer', minHeight: '70px' }}
                        />
                    </FileDropZoneWrapper>
                    <br />
                    <PostAcquisitionImportGrid
                        ref={gridRef}
                    />
                </div>
            </Paper>
        </LoadingOverlayWrapper>
    );
};

const styles = theme => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1)
    },
    leftControls: {
        display: 'flex',
        width: '40%',
        minWidth: 'fit-content',
        alignItems: 'center',
        minHeight: 68
    },
    rightControls: {
        display: 'flex',
        width: '40%',
        justifyContent: 'flex-end',
        marginBottom: 5
    },
    gridPaper: {
        padding: '5px 8px 8px'
    },
    overlay: {
        height: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column'
    },
});

const importType = Constants.ImportType.BACKFILL;

const mapStateToProps = state => {
    const opportunitiesState = importTypeStateSelector(state, Constants.ImportType.BACKFILL);
    const adminModuleState = importModuleStateSelector(state, Constants.Module.ADMIN);
    return {
        importRows: opportunitiesState.importRows,
        importLoadingOverlayState: adminModuleState.importLoadingOverlayState
    }
};

const mapDispatchToProps = (dispatch) => ({
    setImportRawData: (data, source) => dispatch(setImportRawData({ data, importType, source })),
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options)),
    setImportRows: importRows => dispatch(setImportRows({ importType, importRows })),
    redirectToImportStatusTab: history => {
        history.push(Constants.Routes.UNDERWRITE_IMPORT_STATUS);
    },
    setImportLoadingOverlayState: (importLoadingOverlayState) => dispatch(setImportLoadingOverlayState({ importType, importLoadingOverlayState })),
    setErrorListModalState: (errorModal) => dispatch(setErrorListModalState(errorModal)),
    setConfirmationModalState: confirmationModalState => dispatch(setConfirmationModalState(confirmationModalState)),
});

export default compose(
    withRouter,
    withStyles(styles),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(PostAcqImport);